import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button variants tuned for the DealCloser Autopilot dark brand:
 *   - `default` (primary CTA): cobalt bg + hover glow (matches marketing site Get Started)
 *   - `destructive`: amber-red for delete confirms, softened a notch for the dark surface
 *   - `outline`: hairline border, transparent bg, silver text — same as marketing site ghost nav link
 *   - `secondary`: raised surface, used for neutral in-page actions
 *   - `ghost`: no bg, only shows on hover — dense toolbars / icon buttons
 *   - `link`: text-only, cobalt-bright underline on hover
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-[var(--color-cobalt)] text-white shadow-[0_0_0_rgba(28,63,255,0)] hover:bg-[var(--color-cobalt-bright)] hover:shadow-[0_0_24px_rgba(28,63,255,0.45)] hover:-translate-y-[1px]',
        destructive:
          'bg-[var(--color-danger)] text-white shadow-sm hover:bg-[var(--color-danger)]/90',
        outline:
          'border border-[rgba(255,255,255,0.08)] bg-transparent text-[var(--color-silver-bright)] hover:border-[rgba(28,63,255,0.4)] hover:bg-[rgba(28,63,255,0.06)] hover:text-white',
        secondary:
          'bg-[var(--color-surface-raised)] text-[var(--color-silver-bright)] hover:bg-[rgba(255,255,255,0.06)]',
        ghost:
          'text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.04)] hover:text-[var(--color-silver-bright)]',
        link:
          'text-[var(--color-cobalt-bright)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-6 text-[0.9375rem]',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  readonly asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
